import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, ShoppingBag } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { fetchTimeslots } from "@/lib/slots";

export const DeliveryModeDialog = ({ open, onClose }) => {
  const { setDelivery, settings } = useStore();
  const [mode, setMode] = useState("delivery");
  const [postal, setPostal] = useState("");
  const [slotData, setSlotData] = useState({ available: true, options: [], message: null });
  const [slot, setSlot] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      fetchTimeslots().then((d) => { setSlotData(d); setSlot(d.options[0] || ""); });
    }
  }, [open]);

  const zones = settings?.delivery_zones || [];

  const confirm = () => {
    if (!slot) return setError("Aucun créneau disponible pour le moment, réessayez plus tard.");
    if (mode === "delivery") {
      if (!postal.trim()) return setError("Veuillez saisir votre code postal.");
      const zone = zones.find((z) => z.postal_code === postal.trim());
      if (!zone) return setError("Non livrable pour ce code postal, appelez-nous.");
    }
    setDelivery({ mode, postal_code: mode === "delivery" ? postal.trim() : "", timeslot: slot });
    onClose();
  };

  return (
    <Dialog open={open}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
        className="w-[calc(100vw-1rem)] max-w-md max-h-[calc(100dvh-1rem)] flex flex-col rounded-none border-2 border-black p-0 gap-0"
        data-testid="delivery-mode-dialog"
      >
        <DialogHeader className="p-5 border-b border-black">
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">Comment souhaitez-vous commander ?</DialogTitle>
        </DialogHeader>
        <div className="p-5 space-y-5 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="mode-delivery-btn"
              onClick={() => { setMode("delivery"); setError(""); }}
              className={`flex flex-col items-center gap-2 p-4 border-2 transition-colors ${mode === "delivery" ? "border-brand-red bg-brand-red/5" : "border-black"}`}
            >
              <Truck className={`w-7 h-7 ${mode === "delivery" ? "text-brand-red" : ""}`} />
              <span className="font-semibold">Livraison</span>
            </button>
            <button
              data-testid="mode-pickup-btn"
              onClick={() => { setMode("pickup"); setError(""); }}
              className={`flex flex-col items-center gap-2 p-4 border-2 transition-colors ${mode === "pickup" ? "border-brand-red bg-brand-red/5" : "border-black"}`}
            >
              <ShoppingBag className={`w-7 h-7 ${mode === "pickup" ? "text-brand-red" : ""}`} />
              <span className="font-semibold">À emporter</span>
            </button>
          </div>

          {mode === "delivery" && (
            <div className="space-y-2">
              <Label htmlFor="postal">Code postal</Label>
              <Input
                id="postal" inputMode="numeric" autoFocus={false} placeholder="Ex : 1018"
                value={postal} onChange={(e) => { setPostal(e.target.value); setError(""); }}
                className="rounded-none border-black" data-testid="postal-input"
              />
              <p className="text-xs text-muted-foreground">Zones livrées : {zones.map((z) => z.postal_code).join(", ") || "—"}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Créneau horaire</Label>
            {slotData.options.length > 0 ? (
              <div className="grid grid-cols-3 gap-2" data-testid="slot-grid">
                {slotData.options.map((s) => (
                  <button
                    key={s} data-testid={`slot-${s}`} onClick={() => setSlot(s)}
                    className={`text-xs py-2 px-1 border transition-colors ${slot === s ? "bg-black text-white border-black" : "border-black/30 hover:border-black"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-brand-red font-medium" data-testid="slot-closed">{slotData.message || "Fermé pour le moment."}</p>
            )}
          </div>

          {error && <p className="text-sm text-brand-red font-medium" data-testid="delivery-error">{error}</p>}
        </div>
        <div className="p-5 border-t border-black">
          <Button onClick={confirm} data-testid="delivery-confirm-btn" className="w-full rounded-none bg-brand-red hover:bg-brand-red/90 h-12 text-base">
            Voir le menu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
